import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { signalUserEdit } from '@/store/formDrafts';
import { useAnchoredPopup } from '@/shared/hooks/useAnchoredPopup';

/**
 * Легкий попап-календар без зовнішніх залежностей. Єдиний компонент вибору дати
 * у проєкті (замість нативних input type=date / datetime-local).
 * - Стрілки ‹ › для місяця, ‹‹ ›› для року (швидке листання).
 * - Клік по дню — вибір; дабл-клік по дню — вибір + закрити.
 * - withTime=true → поле часу HH:MM, value/onChange у форматі YYYY-MM-DDTHH:MM.
 *   Iнакше value/onChange — YYYY-MM-DD.
 * - ч.939 (рішення власника): дату МОЖНА НАБРАТИ РУКАМИ — юзер вводить лише
 *   ЦИФРИ, крапки (і двокрапку часу) підставляються самі: 17092026 →
 *   17.09.2026. Рік можна дволітерний: 170926 → 17.09.2026. Поки набір
 *   неповний/некоректний — onChange не смикаємо; готову дату віддаємо одразу,
 *   на blur/Enter — нормалізуємо (або повертаємо попереднє значення).
 */
interface Props {
  value: string;                       // YYYY-MM-DD або YYYY-MM-DDTHH:MM
  onChange: (v: string) => void;
  disabled?: boolean;
  withTime?: boolean;
  style?: React.CSSProperties;
}

const MONTHS = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

function pad(n: number): string { return String(n).padStart(2, '0'); }

// Розбити value на date-частину (YYYY-MM-DD) і time-частину (HH:MM).
function splitVal(v: string): { date: string; time: string } {
  if (!v) return { date: '', time: '' };
  const [d, t] = v.split('T');
  return { date: d ?? '', time: (t ?? '').slice(0, 5) };
}
function joinVal(date: string, time: string, withTime: boolean): string {
  if (!date) return '';
  return withTime ? `${date}T${time || '00:00'}` : date;
}
function dateParts(date: string): { y: number; m: number; d: number } | null {
  const p = date ? date.split('-').map(Number) : [];
  if (p.length !== 3 || p.some((n) => Number.isNaN(n))) return null;
  return { y: p[0]!, m: p[1]!, d: p[2]! };
}
/** ч.939: «17092026 1530» → «17.09.2026 15:30» (роздільники самі). */
function maskTyped(raw: string, withTime: boolean): string {
  const dig = raw.replace(/\D/g, '').slice(0, withTime ? 12 : 8);
  let out = dig.slice(0, 2);
  if (dig.length > 2) out += `.${dig.slice(2, 4)}`;
  if (dig.length > 4) out += `.${dig.slice(4, 8)}`;
  if (withTime && dig.length > 8) out += ` ${dig.slice(8, 10)}`;
  if (withTime && dig.length > 10) out += `:${dig.slice(10, 12)}`;
  return out;
}

/** ч.939: набране «дд.мм.рррр[ гг:хх]» → value (YYYY-MM-DD[THH:MM]) або ''. */
function parseTyped(typed: string, withTime: boolean): string {
  const dig = typed.replace(/\D/g, '');
  // рік: 4 цифри (17092026) або 2 (170926 → 20xx)
  if (dig.length !== (withTime ? 12 : 8) && dig.length !== (withTime ? 10 : 6)) return '';
  const d = Number(dig.slice(0, 2));
  const m = Number(dig.slice(2, 4));
  const shortYear = dig.length === (withTime ? 10 : 6);
  const y = shortYear ? 2000 + Number(dig.slice(4, 6)) : Number(dig.slice(4, 8));
  if (!d || !m || m > 12 || y < 1900 || y > 2999) return '';
  if (d > new Date(y, m, 0).getDate()) return '';
  const rest = dig.slice(shortYear ? 6 : 8);
  let time = '';
  if (withTime) {
    const hh = Number(rest.slice(0, 2) || '0');
    const mi = Number(rest.slice(2, 4) || '0');
    if (hh > 23 || mi > 59) return '';
    time = `${pad(hh)}:${pad(mi)}`;
  }
  return joinVal(`${y}-${pad(m)}-${pad(d)}`, time, withTime);
}

function fmtDisplay(v: string, withTime: boolean): string {
  const { date, time } = splitVal(v);
  const dp = dateParts(date);
  if (!dp) return '';
  const base = `${pad(dp.d)}.${pad(dp.m)}.${dp.y}`;
  return withTime && time ? `${base} ${time}` : base;
}

export default function DatePopover({ value, onChange, disabled, withTime = false, style }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // ч.939: типізований текст поля (null — показуємо значення ззовні).
  const [typed, setTyped] = useState<string | null>(null);

  const { date: curDate, time: curTime } = splitVal(value);
  const initDp = dateParts(curDate);
  const [viewY, setViewY] = useState(initDp ? initDp.y : new Date().getFullYear());
  const [viewM, setViewM] = useState(initDp ? initDp.m - 1 : new Date().getMonth());

  useEffect(() => {
    const dp = dateParts(splitVal(value).date);
    if (dp) { setViewY(dp.y); setViewM(dp.m - 1); }
    setTyped(null);  // ч.939: значення змінилось ззовні → показуємо його
  }, [value]);

  // ч.1327: календар — у порталі (таблична частина прокручується вбік і обрізала б його);
  // клік поза полем і поза календарем — закрити.
  const popRef = useRef<HTMLDivElement | null>(null);
  const closePop = useCallback(() => setOpen(false), []);
  const pop = useAnchoredPopup(open && !disabled, ref, popRef, closePop, { estHeight: 300, gap: 4 });

  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const firstWd = (new Date(viewY, viewM, 1).getDay() + 6) % 7;
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWd; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function step(deltaM: number, deltaY: number) {
    let m = viewM + deltaM, y = viewY + deltaY;
    if (m < 0) { m += 12; y -= 1; }
    if (m > 11) { m -= 12; y += 1; }
    setViewM(m); setViewY(y);
  }

  function pick(d: number, close: boolean) {
    // ч.1059: дата з календаря мишею = правка користувача (чернетка).
    signalUserEdit(ref.current);
    setTyped(null);
    onChange(joinVal(`${viewY}-${pad(viewM + 1)}-${pad(d)}`, curTime, withTime));
    if (close) setOpen(false);
  }
  function setTime(t: string) {
    signalUserEdit(ref.current);
    onChange(joinVal(curDate || `${viewY}-${pad(viewM + 1)}-${pad(1)}`, t, withTime));
  }

  const sel = dateParts(curDate);
  const isSel = (d: number) => !!sel && sel.y === viewY && sel.m - 1 === viewM && sel.d === d;
  const isWeekend = (d: number) => (new Date(viewY, viewM, d).getDay() + 6) % 7 >= 5;
  // 2026-08-07 (рішення власника): сьогоднішня дата підсвічується рамкою —
  // кнопка «Сьогодні» стала непотрібною (замінена на «Очистити»).
  const now = new Date();
  const isToday = (d: number) =>
    now.getFullYear() === viewY && now.getMonth() === viewM && now.getDate() === d;

  const btn: React.CSSProperties = {
    padding: '0.15rem 0.4rem', border: '1px solid var(--border)',
    borderRadius: 4, background: 'var(--bg-panel)', cursor: 'pointer', fontSize: '0.85rem',
  };

  return (
    <div ref={ref} className="date-popover" style={{ position: 'relative' }}>
      {/* ч.939: поле редаговане — юзер набирає ЦИФРИ, роздільники самі;
          календар відкривається кнопкою праворуч (клік по полю більше не
          перемикає попап, щоб не заважати набору). */}
      <input
        value={typed ?? fmtDisplay(value, withTime)} disabled={disabled}
        placeholder={withTime ? 'дд.мм.рррр гг:хх' : 'дд.мм.рррр'}
        inputMode="numeric"
        onChange={(e) => {
          const masked = maskTyped(e.target.value, withTime);
          setTyped(masked);
          const parsed = parseTyped(masked, withTime);
          if (parsed) onChange(parsed);           // повна дата — віддаємо одразу
          else if (masked === '') onChange('');   // очищення поля
        }}
        onBlur={() => {
          if (typed === null) return;
          const parsed = parseTyped(typed, withTime);
          if (parsed) onChange(parsed);
          setTyped(null);                          // показати нормалізоване значення
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); setOpen(false); }
          if (e.key === 'Escape') { setTyped(null); setOpen(false); }
        }}
        style={{ width: '100%', paddingRight: '1.8rem', ...style }} />
      <button
        type="button" tabIndex={-1} disabled={disabled}
        title="Календар" aria-label="Календар"
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
          border: 'none', background: 'transparent', padding: '0 0.3rem',
          cursor: disabled ? 'default' : 'pointer', color: 'var(--muted)',
          fontSize: '0.9rem', lineHeight: 1,
        }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      </button>
      {open && !disabled && createPortal(
        <div ref={popRef} className="date-popover__pop" style={{
          ...pop.style,
          background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: 'var(--shadow-lg)', padding: '0.6rem', minWidth: 250,
          color: 'var(--text)', fontSize: '0.9rem', textAlign: 'left',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem', gap: '0.2rem' }}>
            <span style={{ display: 'flex', gap: '0.2rem' }}>
              <button type="button" style={btn} title="Попередній рік" onClick={() => step(0, -1)}>‹‹</button>
              <button type="button" style={btn} title="Попередній місяць" onClick={() => step(-1, 0)}>‹</button>
            </span>
            <b style={{ fontSize: '0.85rem' }}>{MONTHS[viewM]} {viewY}</b>
            <span style={{ display: 'flex', gap: '0.2rem' }}>
              <button type="button" style={btn} title="Наступний місяць" onClick={() => step(1, 0)}>›</button>
              <button type="button" style={btn} title="Наступний рік" onClick={() => step(0, 1)}>››</button>
            </span>
          </div>
          <div className="doc-grid doc-grid--7" style={{ textAlign: 'center' }}>
            {WD.map((w) => (
              <div key={w} style={{ fontSize: '0.7rem', color: 'var(--muted)', fontWeight: 600 }}>{w}</div>
            ))}
            {cells.map((d, i) => d === null ? <div key={i} /> : (
              <div key={i}
                onClick={() => pick(d, false)}
                onDoubleClick={() => pick(d, true)}
                title="Клік — обрати; подвійний клік — обрати й закрити"
                style={{
                  padding: '0.3rem 0', borderRadius: 4, cursor: 'pointer',
                  fontSize: '0.8rem', userSelect: 'none',
                  background: isSel(d) ? 'var(--primary)' : 'transparent',
                  color: isSel(d) ? '#fff' : (isWeekend(d) ? '#dc2626' : 'inherit'),
                  fontWeight: isSel(d) || isToday(d) ? 700 : 400,
                  boxShadow: isToday(d) && !isSel(d)
                    ? 'inset 0 0 0 2px var(--primary)' : undefined,
                }}>
                {d}
              </div>
            ))}
          </div>
          {withTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Час:</span>
              <input type="time" value={curTime || '00:00'}
                onChange={(e) => setTime(e.target.value)}
                style={{ flex: 1 }} />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem' }}>
            <button type="button" style={btn}
              title="Прибрати обрану дату (порожнє поле)"
              onClick={() => { onChange(''); setOpen(false); }}>
              Очистити
            </button>
            <button type="button" style={btn} onClick={() => setOpen(false)}>Закрити</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
